import type {
  SupportMessage,
  SupportTicket,
  TicketMessageAuthorType,
} from './types'

const PREVIEW_LENGTH = 80

const ATTACHMENT_ONLY_SNIPPET = '(attachment)'

const UNNAMED_STAFF = 'Support'

// Mirrors the rule the support service applies when it builds a preview, so a
// row refreshed from a live message reads exactly like one the list endpoint
// sent. Sliced by code point so a cut inside an emoji cannot leave half of it.
export const buildMessagePreview = (body: string): string | null => {
  const collapsed = body.replace(/\s+/g, ' ').trim()
  if (!collapsed) return null
  return Array.from(collapsed).slice(0, PREVIEW_LENGTH).join('')
}

interface PreviewSource {
  body: string
  authorType: TicketMessageAuthorType
  staffName: string | null
}

const withPreviewOf = (
  ticket: SupportTicket,
  source: PreviewSource,
): SupportTicket => ({
  ...ticket,
  lastMessagePreview: buildMessagePreview(source.body),
  lastMessageAuthorType: source.authorType,
  lastMessageStaffName: source.staffName,
})

// An internal note is staff-only and the support service never puts one on this
// channel. The snippet is readable without opening the ticket, so the boundary
// is worth holding here as well as there.
export const withMessagePreview = (
  ticket: SupportTicket,
  message: SupportMessage,
): SupportTicket =>
  message.internal
    ? ticket
    : withPreviewOf(ticket, {
        body: message.body,
        authorType: message.authorType,
        staffName: message.authorType === 'staff' ? message.staffName : null,
      })

// A ticket that was just created comes back with no preview fields, so its row
// would sit a line shorter than every neighbour until the next list fetch. The
// opening message is in the payload that created it.
export const withOpeningPreview = (
  ticket: SupportTicket,
  description: string,
): SupportTicket =>
  withPreviewOf(ticket, {
    body: description,
    authorType: 'user',
    staffName: null,
  })

// Only the ticket list carries previews. Every other ticket payload, a status
// change over the stream above all, leaves them out; merging one must keep what
// the list already knew instead of blanking the row.
export const withPreservedPreview = (
  incoming: SupportTicket,
  existing: SupportTicket | undefined,
): SupportTicket => {
  if (incoming.lastMessageAuthorType !== undefined || !existing) return incoming

  return {
    ...incoming,
    lastMessagePreview: existing.lastMessagePreview,
    lastMessageAuthorType: existing.lastMessageAuthorType,
    lastMessageStaffName: existing.lastMessageStaffName,
  }
}

export const staffFirstName = (staffName: string | null | undefined) =>
  staffName?.trim().split(' ')[0] || UNNAMED_STAFF

const authorPrefix = (ticket: SupportTicket) => {
  if (ticket.lastMessageAuthorType === 'user') return 'You: '
  if (ticket.lastMessageAuthorType === 'staff') {
    return `${staffFirstName(ticket.lastMessageStaffName)}: `
  }
  return ''
}

// An author with no preview text is an attachment-only message, which is worth
// naming: the row says who sent it rather than falling silent.
export const ticketSnippet = (ticket: SupportTicket): string | null => {
  if (!ticket.lastMessageAuthorType) return null
  const preview = ticket.lastMessagePreview ?? ATTACHMENT_ONLY_SNIPPET
  return `${authorPrefix(ticket)}${preview}`
}
